import { useCallback, useState } from 'react';

import { logger } from './logger.js';

/**
 * Thin Web Share API wrapper with a clipboard fallback.
 *
 * Desktops + browsers that don't ship `navigator.share` fall through to
 * `navigator.clipboard.writeText`. The caller decides what to copy
 * (typically the URL).
 *
 * Returns the resolution mode (`'shared' | 'copied' | 'cancelled' | 'failed'`)
 * + an `outcome` state suitable for showing a transient confirmation
 * banner ("Copied!") without managing your own state machine.
 */

export type ShareOutcome = 'idle' | 'shared' | 'copied' | 'cancelled' | 'failed';

interface ShareData {
  readonly title?: string;
  readonly text?: string;
  readonly url: string;
}

export interface UseShareReturn {
  readonly outcome: ShareOutcome;
  readonly share: (data: ShareData) => Promise<ShareOutcome>;
  readonly copy: (text: string) => Promise<ShareOutcome>;
}

export function useShare(): UseShareReturn {
  const [outcome, setOutcome] = useState<ShareOutcome>('idle');

  const share = useCallback(async (data: ShareData): Promise<ShareOutcome> => {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.share === 'function') {
      try {
        await nav.share(data);
        setOutcome('shared');
        return 'shared';
      } catch (e) {
        const err = e as { name?: string };
        if (err?.name === 'AbortError') {
          setOutcome('cancelled');
          return 'cancelled';
        }
        logger.warn('navigator.share failed', { error: String(e) });
        // fall through to clipboard
      }
    }
    return await copyImpl(data.url, setOutcome);
  }, []);

  const copy = useCallback(async (text: string): Promise<ShareOutcome> => {
    return await copyImpl(text, setOutcome);
  }, []);

  return { outcome, share, copy };
}

async function copyImpl(
  text: string,
  setOutcome: (o: ShareOutcome) => void,
): Promise<ShareOutcome> {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  if (!nav || !nav.clipboard) {
    setOutcome('failed');
    return 'failed';
  }
  try {
    await nav.clipboard.writeText(text);
    setOutcome('copied');
    return 'copied';
  } catch (e) {
    logger.warn('clipboard write failed', { error: String(e) });
    setOutcome('failed');
    return 'failed';
  }
}
